import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PetsIcon from '@mui/icons-material/Pets';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Page from "../../components/Page";
import axios from "../../utils/axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

export default function CampaignReport() {
  const { campaignId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);

  // Enhanced color palette with gradients and modern styling
  const chartColors = {
    orange: "#FF6B35", // Vibrant orange for actual delivered
    orangeGradient: "linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)",
    blue: "#4A90E2",   // Modern blue for potential targets  
    blueGradient: "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)",
    lightBlue: "#7FB3D3", // Light blue for pie chart
    darkBlue: "#2C5AA0",  // Dark blue for pie chart
    mediumBlue: "#5B9BD5", // Medium blue for pie chart
    purple: "#8E44AD", // Purple accent
    green: "#27AE60",  // Success green
    lightGray: "#F8F9FA", // Light background
    darkGray: "#2C3E50", // Dark text
  };

  const fetchCampaignReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/campaign/report/${campaignId}`);
      setReportData(response.data.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching campaign report:', error);
      setError('Failed to fetch campaign report data');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchCampaignReport();
  }, [fetchCampaignReport]);

  const processDataForCharts = () => {
    if (!reportData || !reportData.reportingData || reportData.reportingData.length === 0) {
      return { speciesData: {}, totalAgeBreakdown: {} };
    }

    const speciesData = {};
    let totalAgeBreakdown = { '1-4': 0, '4-6': 0, '6-9': 0, '9+': 0 };

    reportData.reportingData.forEach(campaign => {
      const species = campaign.species;
      
      if (!speciesData[species]) {
        speciesData[species] = {
          potentialTargets: 0,
          validTargets: 0,
          actualTargets: 0,
          ageBreakdown: { '1-4': 0, '4-6': 0, '6-9': 0, '9+': 0 },
          campaigns: []
        };
      }

      speciesData[species].potentialTargets += campaign.potentialTargets || 0;
      speciesData[species].validTargets += campaign.validTargets || 0;
      speciesData[species].actualTargets += (campaign.mysqlReportData || []).reduce((sum, data) => sum + (data.total || 0), 0);
      speciesData[species].campaigns.push(campaign);

      // Aggregate age breakdown
      if (campaign.ageBreakdown) {
        Object.keys(campaign.ageBreakdown).forEach(ageRange => {
          speciesData[species].ageBreakdown[ageRange] += campaign.ageBreakdown[ageRange] || 0;
          totalAgeBreakdown[ageRange] += campaign.ageBreakdown[ageRange] || 0;
        });
      }
    });

    return { speciesData, totalAgeBreakdown };
  };

  const downloadReport = async () => {
    if (!reportRef.current) return;

    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`campaign-report-${campaignId}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  if (loading) {
    return (
      <Page title="Campaign Report">
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Campaign Report">
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        </Container>
      </Page>
    );
  }

  if (!reportData) {
    return (
      <Page title="Campaign Report">
        <Container maxWidth="xl">
          <Alert severity="info" sx={{ mt: 3 }}>
            No campaign data available.
          </Alert>
        </Container>
      </Page>
    );
  }

  const { speciesData, totalAgeBreakdown } = processDataForCharts();

  // Prepare data for charts
  const targetsChartData = Object.keys(speciesData).map(species => ({
    species,
    actualDelivered: speciesData[species]?.actualTargets || 0,
    potentialTargets: speciesData[species]?.potentialTargets || 0,
  }));

  // Debug logging
  console.log('Species Data:', speciesData);
  console.log('Targets Chart Data:', targetsChartData);

  // Add fallback data if no data exists or ensure data has values
  let chartData = targetsChartData.length > 0 ? targetsChartData : [
    { species: 'Canine', actualDelivered: 0, potentialTargets: 0 },
    { species: 'Feline', actualDelivered: 0, potentialTargets: 0 }
  ];

  // Ensure all values are numbers and not undefined/null
  chartData = chartData.map(item => ({
    ...item,
    actualDelivered: Number(item.actualDelivered) || 0,
    potentialTargets: Number(item.potentialTargets) || 0
  }));

  console.log('Final Chart Data:', chartData);


  // Use real API data for the chart
  let displayData = Object.keys(speciesData).map(species => ({
    species,
    actualDelivered: speciesData[species]?.actualTargets || 0,
    potentialTargets: speciesData[species]?.potentialTargets || 0,
  }));

  // Fallback to empty data structure if no API data
  if (displayData.length === 0) {
    displayData = [
      { species: 'Canine', actualDelivered: 0, potentialTargets: 0 },
      { species: 'Feline', actualDelivered: 0, potentialTargets: 0 }
    ];
  }
  
  console.log('API Chart Data:', displayData);
  console.log('Species Data from API:', speciesData);

  const totalAgeChartData = [
    { name: '1 to 4', value: totalAgeBreakdown['1-4'], color: chartColors.darkBlue },
    { name: '4 to 6', value: totalAgeBreakdown['4-6'], color: chartColors.orange },
    { name: '6 to 9', value: totalAgeBreakdown['6-9'], color: chartColors.lightBlue },
    { name: '9+', value: totalAgeBreakdown['9+'], color: chartColors.mediumBlue },
  ];

  const getSpeciesAgeData = (species) => {
    const ageData = speciesData[species]?.ageBreakdown || {};
    return [
      { name: '1 to 4', value: ageData['1-4'] || 0, color: chartColors.darkBlue },
      { name: '4 to 6', value: ageData['4-6'] || 0, color: chartColors.orange },
      { name: '6 to 9', value: ageData['6-9'] || 0, color: chartColors.lightBlue },
      { name: '9+', value: ageData['9+'] || 0, color: chartColors.mediumBlue },
    ];
  };


  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            p: 2,
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {payload[0].name}: {payload[0].value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Page title="Campaign Report">
      <Box sx={{ 
        background: `linear-gradient(135deg, ${chartColors.lightGray} 0%, #FFFFFF 100%)`,
        minHeight: '100vh',
        pb: 4
      }}>
      <Container maxWidth="xl">
          <Box ref={reportRef}>
        {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h3" component="h1" gutterBottom>
                Campaign Report Dashboard
              </Typography>
              <Card sx={{ p: 3, mb: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 600 }}>
                  {reportData.originalCampaign.name}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Practice ID:</strong> {reportData.originalCampaign.practiceId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Legacy ID:</strong> {reportData.originalCampaign.legacyId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Scheduled:</strong> {new Date(reportData.originalCampaign.scheduledDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Campaigns:</strong> {reportData.reportingData.length}
              </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadReport}
              sx={{ ml: 2 }}
            >
              Download PDF
            </Button>
        </Box>

          {/* Main Charts Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Targets Chart */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                p: 3, 
                height: '100%',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                }
              }}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ color: chartColors.orange, mr: 1.5, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: chartColors.darkGray, fontWeight: 600 }}>
                      Campaign Targets
                </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: chartColors.darkGray }}>
                    Potential vs Actual Delivered
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    Comparison of potential reach vs actual email delivery
                  </Typography>
                </Box>
                <Box sx={{ height: 400 }}>
                  <div style={{ fontSize: '12px', marginBottom: '10px', color: '#666' }}>
                    Canine={displayData[0]?.actualDelivered}/{displayData[0]?.potentialTargets}, 
                    Feline={displayData[1]?.actualDelivered}/{displayData[1]?.potentialTargets}
                  </div>
                  {displayData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                             data={displayData}
                             margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                             <XAxis 
                               dataKey="species"
                               tick={{ fontSize: 12 }}
                             />
                              <YAxis 
                                domain={[0, 'dataMax + 100']}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => value.toLocaleString()}
                              />
                             <RechartsTooltip 
                               formatter={(value, name) => [value.toLocaleString(), name]}
                             />
                             <Legend />
                             <Bar 
                               dataKey="actualDelivered" 
                               fill="#FF6B35"
                               name="Actual Delivered"
                             />
                             <Bar 
                               dataKey="potentialTargets" 
                               fill="#4A90E2"
                               name="Potential Targets"
                             />
                          </BarChart>
                        </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="h6" color="text.secondary">
                        No data available for chart
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Explanation Text */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="h6" gutterBottom>
                    Potential vs Actual Delivered
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Because this campaign is sent via email, there is a chance that even if the client is within the
                    parameters of the campaign, we are unable to reach them because there is no email address or the email
                    address on file in your system is not a viable email.
                      </Typography>
                  <Typography variant="body2" paragraph>
                    We give you this information so that you are aware of any issues with collecting ALL your client's contact
                    information. <strong>It is important to have their phone number, address, AND email so that you can
                    contact them no matter the mode of communication.</strong>
                      </Typography>
                  <Typography variant="body2">
                    If your number of viable emails are low, we would suggest an initiative in the practice to collect and
                    verify emails of your clients prior to further campaigns. If you would like assistance in how to
                    make this happen, let us know and we can give you some tips to help.
                      </Typography>
                </Box>
              </Card>
            </Grid>

            {/* Overall Age Distribution */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ 
                p: 3, 
                height: '100%',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                }
              }}>
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <PetsIcon sx={{ color: chartColors.blue, mr: 1.5, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: chartColors.darkGray, fontWeight: 600 }}>
                      Age Distribution
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: chartColors.darkGray }}>
                    Overall Pet Ages
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    Age breakdown of all targeted pets
                      </Typography>
                </Box>
                <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                        data={totalAgeChartData}
                              cx="50%"
                              cy="50%"
                        outerRadius={120}
                              dataKey="value"
                            >
                        {totalAgeChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                      <RechartsTooltip content={<PieTooltip />} />
                      <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
              </Card>
            </Grid>
            </Grid>

          {/* Species-Specific Age Distributions */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {Object.keys(speciesData).map(species => (
              <Grid item xs={12} md={6} key={species}>
                <Card sx={{ 
                  p: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: 3,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  border: `2px solid ${species === 'Canine' ? chartColors.blue : chartColors.orange}`,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                  }
                }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <PetsIcon sx={{ 
                        color: species === 'Canine' ? chartColors.blue : chartColors.orange, 
                        mr: 1.5, 
                        fontSize: 28 
                      }} />
                      <Typography variant="h6" sx={{ 
                        color: chartColors.darkGray, 
                        fontWeight: 600 
                      }}>
                        {species} Age Distribution
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Age breakdown for {species.toLowerCase()} patients
                      </Typography>
                  </Box>
                  <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getSpeciesAgeData(species)}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                        >
                          {getSpeciesAgeData(species).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<PieTooltip />} />
                        <Legend />
                      </PieChart>
                        </ResponsiveContainer>
                      </Box>

                  {/* Species Summary */}
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Potential Targets:</strong>
                        </Typography>
                        <Typography variant="h6">
                          {speciesData[species].potentialTargets.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Actual Delivered:</strong>
                        </Typography>
                        <Typography variant="h6">
                          {speciesData[species].actualTargets.toLocaleString()}
                        </Typography>
                  </Grid>
                </Grid>
                  </Box>
              </Card>
            </Grid>
            ))}
            </Grid>

          {/* Campaign Details Table */}
          <Card sx={{ 
            mb: 4,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            borderRadius: 3,
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden'
          }}>
              <Box sx={{ 
              p: 3, 
              background: `linear-gradient(135deg, ${chartColors.darkGray} 0%, ${chartColors.purple} 100%)`,
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 1.5, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Campaign Details Overview
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Comprehensive breakdown of all campaign metrics and performance
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    background: `linear-gradient(135deg, ${chartColors.lightGray} 0%, #e8f0fe 100%)`,
                    '& .MuiTableCell-head': {
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      color: chartColors.darkGray,
                      borderBottom: `2px solid ${chartColors.blue}`,
                      py: 2
                    }
                  }}>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Species</TableCell>
                    <TableCell>Tag</TableCell>
                    <TableCell align="right">Potential</TableCell>
                    <TableCell align="right">Valid</TableCell>
                    <TableCell align="right">Delivered</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...reportData.reportingData].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((campaign, index) => (
                    <TableRow key={campaign.campaignId} sx={{ 
                      '&:nth-of-type(odd)': { 
                        bgcolor: 'rgba(248, 249, 250, 0.5)' 
                      },
                      '&:hover': {
                        bgcolor: 'rgba(74, 144, 226, 0.08)',
                        transform: 'scale(1.01)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s ease'
                      },
                      '& .MuiTableCell-root': {
                        py: 2,
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)'
                      }
                    }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600" sx={{ color: chartColors.darkGray }}>
                          {campaign.campaignName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.species}
                          size="small"
                          sx={{
                            background: campaign.species === 'Canine' 
                              ? `linear-gradient(135deg, ${chartColors.blue} 0%, ${chartColors.mediumBlue} 100%)`
                              : `linear-gradient(135deg, ${chartColors.orange} 0%, #F7931E 100%)`,
                            color: 'white',
                            fontWeight: 600,
                            '& .MuiChip-label': { px: 1.5 }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500" sx={{ color: chartColors.purple }}>
                          {campaign.campaignTag}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" sx={{ color: chartColors.blue }}>
                          {campaign.potentialTargets.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" sx={{ color: chartColors.green }}>
                          {campaign.validTargets.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" sx={{ color: chartColors.orange }}>
                          {campaign.mysqlReportData.reduce((sum, data) => sum + data.total, 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.status}
                          size="small"
                          sx={{
                            background: campaign.status === 'Published' 
                              ? `linear-gradient(135deg, ${chartColors.green} 0%, #2ECC71 100%)`
                              : 'linear-gradient(135deg, #95A5A6 0%, #7F8C8D 100%)',
                            color: 'white',
                            fontWeight: 600,
                            '& .MuiChip-label': { px: 1.5 }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      </Container>
      </Box>
    </Page>
  );
}